import { ExpandableContainer } from '@/src/components/common/ExpandableContainer';
import { useToolPanel } from '@/src/store/tool-panel.context';
import { VirtualMcVoiceType } from '@/src/types/VirtualMcVoiceType';
import { FeatureName, useFeatureUsageTracker, VirtualMcSource } from '@/src/utils/engagement/engagement-tracker';
import { i18nKeys } from '@/src/utils/i18n.utils';
import useInputBuffer from '@/src/hooks/useInputBuffer';
import { clamp } from 'lodash';
import { Box, FormField, HelpPanel, Input, Link, SpaceBetween, Tiles, Toggle } from '@amzn/awsui-components-react';
import { MutableRefObject, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface VirtualMcSettingsProps {
  virtualMcEnabled: boolean | null;
  setVirtualMcEnabled: (virtualMcEnabled: boolean) => void;
  virtualMcVoice: VirtualMcVoiceType | null;
  setVirtualMcVoice: (virtualMcVoice: VirtualMcVoiceType) => void;
  virtualMcFrequency: number | null;
  setVirtualMcFrequency: (virtualMcFrequency: number) => void;
  expandable?: boolean;
  disabled?: boolean;
  setDefaults?: boolean;
  checkValid?: MutableRefObject<() => boolean>;
  source?: VirtualMcSource;
}

export const VirtualMcSettings: React.FC<VirtualMcSettingsProps> = (props) => {
  const { t } = useTranslation();
  const [voiceErrorText, setVoiceErrorText] = useState<string>();
  const [frequencyErrorText, setFrequencyErrorText] = useState<string>();
  const { toggleShowToolPanel, toggleToolPanel, renderToolPanelContent } = useToolPanel();
  const trackFeatureUsage = useFeatureUsageTracker();

  const frequencyInput = useInputBuffer(
    props.virtualMcFrequency,
    (inputValue) => props.setVirtualMcFrequency(clamp(Number(inputValue), 1, 30)),
    { snapBackOnEmpty: true, commitOnChange: true }
  );

  useEffect(() => {
    if (!props.setDefaults) {
      return;
    }

    if (props.virtualMcEnabled === null) {
      props.setVirtualMcEnabled(false);
    }

    if (props.virtualMcVoice === null) {
      props.setVirtualMcVoice(VirtualMcVoiceType.Stephen);
    }

    if (props.virtualMcFrequency === null) {
      props.setVirtualMcFrequency(20);
    }
  }, []);

  useEffect(() => {
    if (!props.checkValid) {
      return;
    }

    props.checkValid.current = () => {
      let valid = true;

      if (props.virtualMcEnabled && !props.virtualMcVoice) {
        valid = false;
        setVoiceErrorText(t(i18nKeys.events.eventDetails.labels.virtualMcVoice.invalidVoice));
      } else {
        setVoiceErrorText(undefined);
      }

      if (props.virtualMcEnabled && !props.virtualMcFrequency) {
        valid = false;
        setFrequencyErrorText(t(i18nKeys.events.eventDetails.labels.virtualMcFrequency.invalidFrequency));
      } else {
        setFrequencyErrorText(undefined);
      }

      return valid;
    };
  });

  useEffect(() => {
    if (props.disabled) {
      setVoiceErrorText(undefined);
      setFrequencyErrorText(undefined);
    }
  }, [props.disabled]);

  const VirtualMcHeaderHelpPanel = () => {
    return (
      <HelpPanel header={t(i18nKeys.events.eventDetails.help.virtualMc.headerHeader)}>
        <SpaceBetween size={'s'}>
          <Box>{t(i18nKeys.events.eventDetails.help.virtualMc.header1)}</Box>
          <Box>{t(i18nKeys.events.eventDetails.help.virtualMc.header2)}</Box>
          <Box>{t(i18nKeys.events.eventDetails.help.virtualMc.header3)}</Box>
        </SpaceBetween>
      </HelpPanel>
    );
  };

  const VirtualMcFrequencyHelpPanel = () => {
    return (
      <HelpPanel header={t(i18nKeys.events.eventDetails.help.virtualMc.frequencyHeader)}>
        <Box>{t(i18nKeys.events.eventDetails.help.virtualMc.frequency1)}</Box>
      </HelpPanel>
    );
  };

  const showHelpPanel = (content: ReactNode) => {
    toggleShowToolPanel(true);
    toggleToolPanel(true);

    renderToolPanelContent(content);
  };

  return (
    <ExpandableContainer
      headerText={t(i18nKeys.events.eventDetails.headers.virtualMc)}
      headerInfo={
        <Link variant="info" onClick={() => showHelpPanel(<VirtualMcHeaderHelpPanel />)}>
          {t(i18nKeys.general.info)}
        </Link>
      }
      expandable={props.expandable}
      data-testid="virtualmc-settings-container"
    >
      <SpaceBetween size="s">
        <Toggle
          onChange={({ detail }) => {
            props.setVirtualMcEnabled(detail.checked);
            trackFeatureUsage(
              FeatureName.VIRTUAL_MC,
              detail.checked ? 'enable' : 'disable',
              props.source ? { source: props.source } : undefined
            );
          }}
          checked={props.virtualMcEnabled || false}
          description={t(i18nKeys.events.eventDetails.labels.virtualMcToggle.description)}
          disabled={props.disabled}
          data-testid="virtualmc-toggle"
        >
          {t(i18nKeys.events.eventDetails.labels.virtualMcToggle.label)}
        </Toggle>
        {props.virtualMcEnabled && (
          <>
            <FormField label={t(i18nKeys.events.eventDetails.labels.virtualMcVoice.label)} errorText={voiceErrorText}>
              <Tiles
                onChange={({ detail }) => props.setVirtualMcVoice(detail.value as VirtualMcVoiceType)}
                value={props.virtualMcVoice || null}
                items={[
                  {
                    label: 'Stephen',
                    description: t(i18nKeys.events.eventDetails.labels.virtualMcVoice.stephenDescription),
                    value: VirtualMcVoiceType.Stephen,
                    disabled: props.disabled,
                  },
                  {
                    label: 'Ruth',
                    description: t(i18nKeys.events.eventDetails.labels.virtualMcVoice.ruthDescription),
                    value: VirtualMcVoiceType.Ruth,
                    disabled: props.disabled,
                  },
                ]}
                data-testid="virtualmc-voice-tiles"
              />
            </FormField>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="s">
                  {t(i18nKeys.events.eventDetails.labels.virtualMcFrequency.label)}
                  <Link variant="info" onClick={() => showHelpPanel(<VirtualMcFrequencyHelpPanel />)}>
                    {t(i18nKeys.general.info)}
                  </Link>
                </SpaceBetween>
              }
              description={t(i18nKeys.events.eventDetails.labels.virtualMcFrequency.description)}
              errorText={frequencyErrorText}
            >
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Input
                  type="number"
                  value={frequencyInput.value}
                  onChange={(e) => frequencyInput.handleChange(e.detail.value)}
                  onBlur={frequencyInput.handleBlur}
                  disabled={props.disabled}
                  data-testid="virtualmc-frequency-input"
                />
                <span>{t(i18nKeys.time.minutes)}</span>
              </SpaceBetween>
            </FormField>
          </>
        )}
      </SpaceBetween>
    </ExpandableContainer>
  );
};
